// Shared, stateless helpers and class strings used across multiple screens.

// ─── Shared underline field classes ──────────────────────────────────────────
export const labelBase = 'font-dm-sans text-[13px] uppercase tracking-[0.14em] font-medium text-white/60 group-focus-within:text-[rgba(212,168,67,0.85)] transition-colors duration-300 ease-[cubic-bezier(0.23,1,0.32,1)]';
export const borderBase = 'border-b border-white/[0.13] group-focus-within:border-[rgba(212,168,67,0.55)] pb-2.5 transition-colors duration-300 ease-[cubic-bezier(0.23,1,0.32,1)]';
export const inputBase = 'w-full bg-transparent text-white font-dm-sans text-[15px] font-light outline-none caret-[#D4A843]';
export const counterBtn = 'w-6 h-6 rounded-full border border-[rgba(212,168,67,0.35)] text-[#D4A843] hover:border-[rgba(212,168,67,0.65)] hover:bg-[rgba(212,168,67,0.06)] active:scale-[0.9] transition-[border-color,background-color,transform] duration-150 flex items-center justify-center font-dm-sans text-sm leading-none shrink-0 select-none';

// ─── Online booking window ────────────────────────────────────────────────────
// How many days beyond IST today each flow may book. The SAME numbers are
// enforced server-side (supabase/functions/_shared/bookingWindow.ts) by the
// hold and availability functions — the cards here only decide what is
// offered; the server decides what is accepted. Change both together.
export const CUSTOMER_WINDOW_DAYS = 7; // today + 7 → 8 cards
export const PARTNER_WINDOW_DAYS = 9;  // approved partners: today + 9 → 10 cards

// "Today" is the IST calendar date, not the browser's — a guest abroad (or a
// device with a wrong clock zone) must see the same window the server checks.
function istTodayParts() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const get = (type) => Number(parts.find((x) => x.type === type)?.value);
  return { y: get('year'), m: get('month'), d: get('day') };
}

// A calendar date with no time-zone drift: built at UTC noon-less midnight
// and read back with UTC getters, so adding days never crosses a DST edge.
function utcDate(y, m, d) {
  return new Date(Date.UTC(y, m - 1, d));
}

function isoOf(date) {
  return date.toISOString().slice(0, 10); // YYYY-MM-DD
}

// Parse YYYY-MM-DD → UTC Date (null for anything else).
function parseIso(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  return m ? utcDate(Number(m[1]), Number(m[2]), Number(m[3])) : null;
}

// IST today as YYYY-MM-DD — the date every "is this change live yet" test
// is made against, so a guest abroad sees the same notice as one in Guwahati.
export function istTodayIso() {
  const { y, m, d } = istTodayParts();
  return isoOf(utcDate(y, m, d));
}

// YYYY-MM-DD plus/minus whole days ('' for a malformed input).
export function addDaysIso(iso, days) {
  const date = parseIso(iso);
  if (!date) return '';
  return isoOf(utcDate(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate() + days));
}

// Whole days from one YYYY-MM-DD to another (positive when `toIso` is later);
// null if either is malformed.
export function daysBetweenIso(fromIso, toIso) {
  const from = parseIso(fromIso);
  const to = parseIso(toIso);
  if (!from || !to) return null;
  return Math.round((to - from) / 86400000);
}

// Long form for summaries and tickets: "Monday, 29 September 2026".
export function formatCardDate(iso) {
  const date = parseIso(iso);
  if (!date) return '';
  const weekday = date.toLocaleDateString('en-GB', { weekday: 'long', timeZone: 'UTC' });
  const month = date.toLocaleDateString('en-GB', { month: 'long', timeZone: 'UTC' });
  return `${weekday}, ${date.getUTCDate()} ${month} ${date.getUTCFullYear()}`;
}

// The three lines of a compact date card: TODAY / TOMORROW / weekday on top,
// the day number large in the middle, the month underneath — so no two cards
// can ever read the same, however wide the window.
function cardParts(date, offset) {
  const weekday = date.toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' }).toUpperCase();
  // en-GB abbreviates September as "Sept"; keep every month to three letters.
  const month = date.toLocaleDateString('en-GB', { month: 'short', timeZone: 'UTC' }).slice(0, 3).toUpperCase();
  const top = offset === 0 ? 'TODAY' : offset === 1 ? 'TOMORROW' : weekday;
  return { top, day: date.getUTCDate(), month };
}

// The bookable dates for a flow: IST today plus `windowDays` more. Every card
// carries its ISO date, and the selected date is stored AS that ISO string
// (never as a position), so no screen can resolve a different day than the
// one the guest picked. Callers pass CUSTOMER_WINDOW_DAYS or PARTNER_WINDOW_DAYS.
export function getDateCards(windowDays) {
  const { y, m, d } = istTodayParts();
  return Array.from({ length: windowDays + 1 }, (_, offset) => {
    const date = utcDate(y, m, d + offset);
    const { top, day, month } = cardParts(date, offset);
    return {
      top,                              // 'TODAY' | 'TOMORROW' | 'THU'
      day,                              // 24
      month,                            // 'SEP'
      label: `${top} ${day} ${month}`,  // one-line form, for accessibility text
      formatted: formatCardDate(isoOf(date)),
      iso: isoOf(date), // YYYY-MM-DD, for backend queries and as the stored selection
    };
  });
}

// ─── Clock time display ───────────────────────────────────────────────────────
// "17:00" / "17:00:00" (cruise_settings) → "5:00 PM". Sailing and reporting
// times are ALWAYS read from cruise_settings and formatted with this — never
// typed into the UI, where they drift from the real schedule.
export function to12h(t) {
  if (!t) return '';
  const [hRaw, mRaw] = String(t).split(':');
  let h = parseInt(hRaw, 10);
  if (Number.isNaN(h)) return '';
  const m = (mRaw ?? '00').padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${ampm}`;
}

// ─── Deck display label ───────────────────────────────────────────────────────
// DISPLAY ONLY. The Dinner Cruise runs on the Middle Deck because that is where
// the performances happen, so guests see it named for what it is. The stored /
// queried value stays 'Middle Deck' everywhere (holds, pricing, bookings,
// gallery keys, analytics) — never pass this label to the backend. Sunset and
// Sundown keep both real deck names since guests genuinely choose between them.
// Edge-function twin: supabase/functions/_shared/deckLabel.ts.
export function displayDeck(cruiseName, deck) {
  if (cruiseName === 'Dinner Cruise' && deck === 'Middle Deck') return 'Performance Deck';
  return deck;
}

// ─── Cruise display label ─────────────────────────────────────────────────────
// DISPLAY ONLY. cruise_settings.cruise_name is the join key every table, RPC
// and trigger compares on and never changes. What guests SEE lives in the same
// row: cruise_settings.display_name and display_name_short (migration
// 20261008120000_cruise_display_names.sql). Null or blank falls back to
// cruise_name, so a cruise with no display name shows its stored name and there
// is no name table in code.
// Takes the cruise_settings row (snake_case) or the availability function's
// cruise entry (camelCase: cruiseName / displayName / displayNameShort).
// Never pass the result into a query, a hold, a booking row, an analytics event
// or a template variable that logic keys on — those take cruise_name.
// Edge-function twin: supabase/functions/_shared/cruiseLabel.ts.
export function cruiseLabel(cruise, form = 'long') {
  if (!cruise) return '';
  const stored = cruise.cruise_name ?? cruise.cruiseName ?? '';
  const chosen = form === 'short'
    ? (cruise.display_name_short ?? cruise.displayNameShort)
    : (cruise.display_name ?? cruise.displayName);
  const label = typeof chosen === 'string' ? chosen.trim() : '';
  return label || stored;
}
